import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

type Props = InputHTMLAttributes<HTMLInputElement> & {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  prefix?: ReactNode;
};

export const Input = forwardRef<HTMLInputElement, Props>(function Input(
  { label, hint, error, prefix, className = '', id, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={inputId} className="text-sm text-[var(--color-text-muted)]">
          {label}
        </label>
      )}
      <div
        className={[
          'flex items-stretch overflow-hidden',
          'border rounded-[var(--radius-sm)]',
          error ? 'border-[#a4322a] bg-[var(--color-red-tint)]' : 'border-[var(--color-border)] bg-[var(--color-surface)]',
          'focus-within:outline-2 focus-within:outline-[var(--color-blue)] focus-within:outline',
        ].join(' ')}
      >
        {prefix && (
          <span className="flex items-center px-3 text-sm text-[var(--color-text-muted)] bg-[var(--color-page)] border-r border-[var(--color-border)]">
            {prefix}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          className={[
            'flex-1 bg-transparent px-3 py-2 text-sm outline-none',
            className,
          ].join(' ')}
          {...rest}
        />
      </div>
      {(hint || error) && (
        <span className={['text-xs', error ? 'text-[#a4322a]' : 'text-[var(--color-text-muted)]'].join(' ')}>
          {error ?? hint}
        </span>
      )}
    </div>
  );
});
